import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Users, Map, DollarSign, TrendingUp, 
  UserCheck, Plane, Calendar, Activity,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'orange' }) => {
  const colorClasses = {
    orange: 'from-orange-500 to-red-500',
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-green-500 to-emerald-500',
    purple: 'from-purple-500 to-pink-500',
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-slate-400 text-sm mb-1">{title}</p>
            <p className="text-3xl font-bold text-white">{value}</p>
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AdminDashboard() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400">Visão geral da plataforma ViajeJunto</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total de Usuários"
          value={stats?.total_users || 0}
          icon={Users}
          trend="up"
          trendValue={`+${stats?.new_users_week || 0} esta semana`}
          color="blue"
        />
        <StatCard
          title="Usuários Ativos"
          value={stats?.active_users || 0}
          icon={UserCheck}
          color="green"
        />
        <StatCard
          title="Organizadores"
          value={stats?.total_organizers || 0}
          icon={Plane}
          color="purple"
        />
        <StatCard
          title="Total de Viagens"
          value={stats?.total_trips || 0}
          icon={Map}
          trend="up"
          trendValue={`+${stats?.new_trips_week || 0} esta semana`}
          color="orange"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400">Status das Viagens</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Ativas</span>
                <Badge className="bg-green-500/20 text-green-400">{stats?.active_trips || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Concluídas</span>
                <Badge className="bg-blue-500/20 text-blue-400">{stats?.completed_trips || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Total</span>
                <Badge className="bg-slate-500/20 text-slate-400">{stats?.total_trips || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400">Financeiro</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Total em Pagamentos</span>
                <span className="text-white font-bold">
                  R$ {(stats?.total_payments || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Total Pago</span>
                <span className="text-green-400 font-bold">
                  R$ {(stats?.total_paid || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Taxa de Conversão</span>
                <span className="text-orange-400 font-bold">
                  {stats?.total_payments > 0 
                    ? ((stats?.total_paid / stats?.total_payments) * 100).toFixed(1) 
                    : 0}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400">Hoje</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Novos Usuários</span>
                <Badge className="bg-orange-500/20 text-orange-400">+{stats?.new_users_today || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Novas Viagens</span>
                <Badge className="bg-orange-500/20 text-orange-400">+{stats?.new_trips_today || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Ações Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a 
              href="/admin/users" 
              className="p-4 rounded-xl bg-slate-700/50 hover:bg-slate-700 transition-colors flex flex-col items-center gap-2"
            >
              <Users className="w-6 h-6 text-blue-400" />
              <span className="text-sm text-slate-300">Ver Usuários</span>
            </a>
            <a 
              href="/admin/trips" 
              className="p-4 rounded-xl bg-slate-700/50 hover:bg-slate-700 transition-colors flex flex-col items-center gap-2"
            >
              <Map className="w-6 h-6 text-green-400" />
              <span className="text-sm text-slate-300">Ver Viagens</span>
            </a>
            <a 
              href="/admin/analytics" 
              className="p-4 rounded-xl bg-slate-700/50 hover:bg-slate-700 transition-colors flex flex-col items-center gap-2"
            >
              <TrendingUp className="w-6 h-6 text-purple-400" />
              <span className="text-sm text-slate-300">Analytics</span>
            </a>
            <a 
              href="/admin/activity" 
              className="p-4 rounded-xl bg-slate-700/50 hover:bg-slate-700 transition-colors flex flex-col items-center gap-2"
            >
              <Activity className="w-6 h-6 text-orange-400" />
              <span className="text-sm text-slate-300">Atividades</span>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
